import { motion } from 'framer-motion';
import { styled, css } from 'styled-components';
import { Media } from '../../../../../theme/Media';
import { BORDER_RADIUS, CSS_VAR_SCROLL_LEFT, CSS_VAR_VISIBLE_WIDTH, DATA_ATTR_CAN_SCROLL_LEFT, DATA_ATTR_CONTENT_TYPE, DATA_ATTR_IS_EXPANDED, DATA_ATTR_TABLE_GROUP, GROUP_INDICATOR_WIDTH, GROUP_SPACING, MOBILE_PADDING, TABLE_ROW_EXPANDING_DURATION_MS, Z_INDEX_EXPANDED_OUTLINE, Z_INDEX_GROUP_INDICATOR, } from '../../constants';
import { TableCellContentType } from '../../types';
import { SubRowsContainer } from '../SubRow/styled';
export const RowInner = styled.div `
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
  position: relative;
  overflow: clip;
  transition: border-radius ${TABLE_ROW_EXPANDING_DURATION_MS}ms ease-in-out;

  background: ${({ theme }) => theme.colors.grey00_1};

  ${({ $isInteractive }) => $isInteractive &&
    css `
      cursor: pointer;
    `}

  ${({ theme, $flash }) => $flash &&
    css `
      animation: highlight-row 4s ease-in-out;

      & > div[role='cell'][${DATA_ATTR_CONTENT_TYPE}='${TableCellContentType.DND}'],
      & > div[role='cell'][${DATA_ATTR_CONTENT_TYPE}='${TableCellContentType.CHECKBOX}'],
      & > div[role='cell'][${DATA_ATTR_CONTENT_TYPE}='${TableCellContentType.ACTION}'] {
        animation: highlight-row 4s ease-in-out;
      }

      @keyframes highlight-row {
        0%,
        100% {
          background: ${theme.colors.grey00_1};
        }
        10%,
        75% {
          background: ${theme.colors.grey01_1};
        }
      }
    `}

  ${({ theme, $highlighted, $flash }) => $highlighted &&
    !$flash &&
    css `
      background: ${theme.colors.grey01_1};
    `}

  @media ${Media.smMin} {
    grid-row: 1;

    ${({ theme, $withCellDividers }) => $withCellDividers &&
    css `
        &
          > div[role='cell']:not([${DATA_ATTR_CONTENT_TYPE}='${TableCellContentType.DND}']):not(
            [${DATA_ATTR_CONTENT_TYPE}='${TableCellContentType.CHECKBOX}']
          ):not([${DATA_ATTR_CONTENT_TYPE}='${TableCellContentType.ACTION}'])
          + div[role='cell']:not([${DATA_ATTR_CONTENT_TYPE}='${TableCellContentType.DND}']):not(
            [${DATA_ATTR_CONTENT_TYPE}='${TableCellContentType.CHECKBOX}']
          ):not([${DATA_ATTR_CONTENT_TYPE}='${TableCellContentType.ACTION}']) {
          border-left: 1px solid ${theme.colors.grey10_1};
        }
      `}
  }

  @media ${Media.xsMax} {
    display: block;
    -webkit-tap-highlight-color: transparent;
  }
`;
export const RowWrapper = styled.div `
  display: grid;
  grid-template-columns: subgrid;
  grid-template-rows: auto auto auto;
  grid-column: 1 / -1;
  overflow: clip;
  transition: border-radius ${TABLE_ROW_EXPANDING_DURATION_MS}ms ease-in-out;
  position: relative;

  ${({ $isDragging }) => $isDragging &&
    css `
      visibility: hidden;
    `}

  ${({ theme, $isInteractive }) => $isInteractive &&
    css `
      &:hover > ${RowInner}, &:hover > ${SubRowsContainer} {
        background: ${theme.colors.grey01_1};
      }
    `}

  ${({ theme, $highlighted, $flash }) => $highlighted &&
    !$flash &&
    css `
      & > ${SubRowsContainer} {
        background: ${theme.colors.grey01_1};
      }
    `}

  ${({ theme, $flash }) => $flash &&
    css `
      & > ${SubRowsContainer} {
        animation: highlight-subrow 4s ease-in-out;
      }

      @keyframes highlight-subrow {
        0%,
        100% {
          background: ${theme.colors.grey00_1};
        }
        10%,
        75% {
          background: ${theme.colors.grey01_1};
        }
      }
    `}

  @media ${Media.xsMax} {
    display: block;
    border-radius: ${BORDER_RADIUS};
    overflow: hidden;

    ${({ theme, $inner }) => $inner
    ? css `
            border: 1px solid ${theme.colors.grey10_1};
          `
    : theme.shadows.small}

    & + & {
      margin-top: ${GROUP_SPACING};
    }
  }
`;
export const GroupIndicator = styled.div `
  display: none;

  [${DATA_ATTR_TABLE_GROUP}] & {
    display: block;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: ${GROUP_INDICATOR_WIDTH};
    background: ${({ theme }) => theme.colors.lightBlue30};
    z-index: ${Z_INDEX_GROUP_INDICATOR};
    pointer-events: none;
  }

  @media ${Media.smMin} {
    [${DATA_ATTR_TABLE_GROUP}] & {
      position: sticky;
      grid-row: 1 / -1;
      grid-column: 1;
      align-self: stretch;
      top: unset;
      bottom: unset;
    }

    [${DATA_ATTR_CAN_SCROLL_LEFT}='true'] [${DATA_ATTR_TABLE_GROUP}] ${RowWrapper}:not(:has([${DATA_ATTR_CONTENT_TYPE}='${TableCellContentType.DND}'])):not(:has([${DATA_ATTR_CONTENT_TYPE}='${TableCellContentType.CHECKBOX}'])) & {
      ${({ theme }) => theme.shadows.medium}
    }
  }
`;
export const ExpandedOutline = styled.div `
  position: absolute;
  inset: 0;
  border: 3px solid ${({ theme }) => theme.colors.mainLight};
  border-radius: ${BORDER_RADIUS};
  pointer-events: none;
  z-index: ${Z_INDEX_EXPANDED_OUTLINE};
  opacity: 0;
  transition: opacity ${TABLE_ROW_EXPANDING_DURATION_MS}ms ease-in-out;

  [${DATA_ATTR_IS_EXPANDED}='true'] & {
    opacity: 1;
  }

  @media ${Media.smMin} {
    top: 0;
    bottom: 0;
    left: var(${CSS_VAR_SCROLL_LEFT}, 0px);
    right: unset;
    width: var(${CSS_VAR_VISIBLE_WIDTH}, 100%);
    box-sizing: border-box;
  }
`;
export const ExpandableMotion = styled(motion.div) `
  grid-column: 1 / -1;
  grid-row: 3;
  border-top: 1px solid ${({ theme }) => theme.colors.grey10_1};
  overflow: hidden;
`;
export const ExpandableContent = styled.div `
  padding: ${MOBILE_PADDING};
  background: ${({ theme }) => theme.colors.grey01_1};
`;
